import React, { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { Card, CardContent } from "./Card";
import { Badge } from "./Badge";
import { GoDotFill } from "react-icons/go";
import { FaHouseChimney } from "react-icons/fa6";
import { AllPosts } from "../../types/posts";
import { displayStatus, statusLabel } from "./postStatusLabel";
import { useNavigate } from "react-router-dom";
import { getPlatformIcon } from "../../utils/logo-getter";
import SimpleTooltip from "../misc/SimpleTooltip";

interface PostCardProps {
    post: AllPosts;
    idx: number;
    date?: string;
    /**
     * When true, hides the per-card status row (the colored dot + status label).
     * Use it when the surrounding surface already communicates the status —
     * e.g. the floating "Processing" panel labels every tile as Processing
     * in its header, so the per-tile row is redundant noise.
     *
     * Also expands the time label to include the scheduled date so the user
     * has full context outside the calendar grid.
     */
    processingPreview?: boolean;
}

const PostCard: React.FC<PostCardProps> = ({
    post,
    idx,
    date,
    processingPreview = false,
}) => {
    const navigate = useNavigate();

    const titleRef = useRef<HTMLSpanElement>(null);
    const [isTitleTruncated, setIsTitleTruncated] = useState(false);

    const displayTitle =
        post.contentType === "TextPost"
            ? post.type
            : post.contentType
                  .replace(/([A-Z])/g, " $1")
                  .replace(/\b\w/g, (c) => c.toUpperCase());

    // Only enable the tooltip when the title is actually clipped, so short
    // titles (e.g. "General") don't get a redundant hover bubble.
    useEffect(() => {
        const el = titleRef.current;
        if (!el) return;
        setIsTitleTruncated(el.scrollWidth > el.clientWidth);
    }, [displayTitle]);

    const scheduled = new Date(post.scheduledFor);
    const postTime = processingPreview
        ? format(scheduled, "MMM d • h:mm a")
        : format(scheduled, "h:mm a");

    const getStatusColor = (status: string) => {
        switch (status.toUpperCase()) {
            case "DRAFT":
                return "#9c27b0";
            case "SCHEDULED":
                return "#FF8E15";
            // MB-1438: RETRYING (MB-1330 backoff lane) surfaces as its own
            // "Pending Retry" badge - deliberately reversing the old
            // shown-as-Scheduled masking: "Scheduled" misled once the original
            // time passed, and a terminal-looking ERROR that later self-healed
            // caused support pings.
            case "RETRYING":
                return "#FF5722";
            case "POSTED":
                return "#69B34C";
            case "ERROR":
            case "VIDEOERROR":
                return "#FF0D0D";
            case "PAUSED":
                return "#ffe11a";
            case "UNPOSTED":
                return "#8C8C8C";
            case "VIDEOPROCESSING":
            case "SOCIALPAUSE":
                return "#512DA8";
            default:
                return "#000000";
        }
    };

    const handlePostNavigation = () => {
        navigate("/post", {
            state: { postId: post.id },
        });
    };
    return (
        <div>
            <Card
                id={`${date}-post-${idx}`}
                className="mb-2 hover:bg-gray-50 h-full cursor-pointer"
                leftBorderColor={post.color}
                onClick={handlePostNavigation}
            >
                <CardContent className="p-2">
                    <div className="flex justify-between items-center">
                        <SimpleTooltip
                            message={displayTitle}
                            visible={isTitleTruncated}
                            className="flex-1 min-w-0"
                        >
                            <span
                                ref={titleRef}
                                className="font-medium text-base truncate block w-full"
                            >
                                {displayTitle}
                            </span>
                        </SimpleTooltip>
                        <span className="text-xs ml-2 text-gray-500 whitespace-nowrap">
                            {postTime}
                        </span>
                    </div>
                    <div className="flex flex-row items-center mb-1">
                        {/* MB-1274: house glyph replaces the status dot when
                            this post will be auto-enriched with open-house
                            info at publish. Same size + status color — the
                            shape is the signal, the card never grows. Absent
                            flag falls through to the dot, so an older api is
                            a graceful no-op. */}
                        {post.hasUpcomingOpenHouses ? (
                            <FaHouseChimney
                                size={22}
                                color={getStatusColor(displayStatus(post))}
                                title="Open house info will be added when this publishes"
                            />
                        ) : (
                            <GoDotFill
                                size={22}
                                color={getStatusColor(displayStatus(post))}
                            />
                        )}
                        <span className="px-2 py-0.5 rounded-full text-xs w-full truncate">
                            {statusLabel(displayStatus(post))}
                        </span>
                        {/* MB-1399: developer testing mode — published through
                            the pipeline but never reached Ayrshare. */}
                        {post.simulated && (
                            <span className="ml-1 shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium text-white">
                                Simulated
                            </span>
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                        {getPlatformIcon(post.platform)}
                        <Badge
                            variant="secondary"
                            className="flex-1"
                            style={{ backgroundColor: post.color }}
                        >
                            <span
                                className="overflow-hidden text-ellipsis whitespace-nowrap"
                                style={{
                                    color: (() => {
                                        let hex = post.color;
                                        if (hex[0] === "#") hex = hex.slice(1);
                                        const r = parseInt(
                                            hex.substring(0, 2),
                                            16
                                        );
                                        const g = parseInt(
                                            hex.substring(2, 4),
                                            16
                                        );
                                        const b = parseInt(
                                            hex.substring(4, 6),
                                            16
                                        );
                                        const brightness =
                                            (r * 299 + g * 587 + b * 114) /
                                            1000;
                                        return brightness > 186
                                            ? "black"
                                            : "white";
                                    })(),
                                }}
                            >
                                {post.type.toLowerCase() === "listing"
                                    ? post?.address?.split(",")[0]
                                    : post.type.charAt(0).toUpperCase() +
                                      post.type.slice(1)}
                            </span>
                        </Badge>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default PostCard;
